import { createSlice } from "@reduxjs/toolkit";
import { getState } from "@bd-stores/store";

/**
 * @type {BD_StoreGameProgress}
 */
const initialState = {
    players: [],
    finalTeamAnswers: {},
    teamAnswers: {},
    roomStatus: {},
    roomsStartedAt: {},
    documents : {},
    allRoomCompleted: false,
    isFinished: false,
    isMiddleState: false,
    currentRoomStartedAt: null,
};

const gameProgressSlice = createSlice({
    name: "gameProgress",
    initialState,
    reducers: {
        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['players']>} action
         */
        setPlayers: (state, action) => ({
            ...state,
            players: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['finalTeamAnswers']>} action
         */
        setFinalTeamAnswers: (state, action) => ({
            ...state,
            finalTeamAnswers: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['teamAnswers']>} action
         */
        setTeamAnswers: (state, action) => ({
            ...state,
            teamAnswers: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['roomStatus']>} action
         */
        setRoomStatus: (state, action) => ({
            ...state,
            roomStatus: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['roomsStartedAt']>} action
         */
        setRoomsStartedAt: (state, action) => ({
            ...state,
            roomsStartedAt: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['allRoomCompleted']>} action
         */
        setAllRoomCompleted: (state, action) => ({
            ...state,
            allRoomCompleted: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['isFinished']>} action
         */
        setFinished: (state, action) => ({
            ...state,
            isFinished: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['isMiddleState']>} action
         */
        setIsMiddleState: (state, action) => ({
            ...state,
            isMiddleState: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['documents']>} action
         */
        setDocuments: (state, action) => ({
            ...state,
            documents: action.payload,
        }),

        setCurrentRoomStartedAt: (state, action) => ({
            ...state,
            currentRoomStartedAt: action.payload,
        }),
    },
});

export const getRoomStatus = (roomId) => {
    const roomStatus = getState().gameProgress.roomStatus;
    return roomStatus?.[roomId] || "is-status-open";
};

export const getFinalTeamAnswers = (roomId) => {
    const finalTeamAnswers = getState().gameProgress.finalTeamAnswers;
    return finalTeamAnswers?.[roomId] || false;
};

export const getTeamAnswers = (roomId) => {
    const teamAnswers = getState().gameProgress.teamAnswers;
    return teamAnswers?.[roomId] || false;
};

export const getDocuments = () => {
    const documents = getState().gameProgress.documents;
    return documents ?? [];
}

export const getPlayers = () => {
    const players = getState().gameProgress.players;
    return players ?? [];
};

export const getUserFromId = (userId) => {
    const players = getState().gameProgress.players;
    const user = players.find((p) => p.userId === userId);
    return user;
};

export const getRoomStartedAt = (roomId, userId) => {
    const roomStartedAt = getState().gameProgress.roomsStartedAt;
    return roomStartedAt?.[roomId]?.[userId] || null;
}

export const {
    setPlayers,
    setTeamAnswers,
    setFinalTeamAnswers,
    setRoomStatus,
    setRoomsStartedAt,
    setAllRoomCompleted,
    setFinished,
    setIsMiddleState,
    setDocuments,
    setCurrentRoomStartedAt,
} = gameProgressSlice.actions;
export const gameProgress = gameProgressSlice.reducer;
